import React, { useEffect, useState, useContext } from "react";
import { Button, Table, Modal, Form, Spinner, Alert, Badge } from "react-bootstrap";
import { AuthContext } from "../../hooks/auth/AuthContext";
import {
    getUsuarios,
    getUsuarioById,
    createUsuario,
    updateUsuario,
    deleteUsuario,
    getRoles,
    getTenants
} from "../../services/dataService";
import Head from "../../components/layout/head/Head";
import "../../styles/usuarios-page-styles.css";
import { toast } from "react-toastify";

const UsuariosPage = () => {
    const authContext = useContext(AuthContext);
    const { user } = authContext;

    const [usuarios, setUsuarios] = useState([]);
    const [tenants, setTenants] = useState([]);
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [currentUsuario, setCurrentUsuario] = useState(null);
    const [incluirInactivos, setIncluirInactivos] = useState(false);

    // Form state
    const [formData, setFormData] = useState({
        id: "",
        nombre: "",
        apellido: "",
        email: "",
        password: "",
        rol: "",
        tenantIds: [],
        activo: true,
    });

    const [formErrors, setFormErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        loadData();
        // eslint-disable-next-line
    }, [incluirInactivos]);

    const loadData = async () => {
        try {
            setLoading(true);
            
            // Admin no necesita cargar tenants (se asigna automáticamente)
            const isOwner = user?.roles?.includes("Owner");
            
            const promises = [
                getUsuarios(user.token, incluirInactivos),
                getRoles(user.token)
            ];
            
            // Solo Owner carga la lista de tenants
            if (isOwner) {
                promises.push(getTenants(user.token, true));
            }
            
            const results = await Promise.all(promises);
            
            console.log('Usuarios recibidos:', results[0]); // Debug
            setUsuarios(results[0]);
            setRoles(results[1]);
            
            if (isOwner) {
                setTenants(results[2]);
            } else {
                setTenants([]); // Admin no necesita tenants
            }
        } catch (error) {
            console.error("Error al cargar datos:", error);
            toast.error("Error al cargar los datos");
        } finally {
            setLoading(false);
        }
    };

    const handleShowModal = async (usuario = null) => {
        if (usuario) {
            setEditMode(true);
            setCurrentUsuario(usuario);
            
            // Cargar detalles completos del usuario
            try {
                const usuarioCompleto = await getUsuarioById(usuario.id, user.token);
                console.log('Usuario cargado:', usuarioCompleto); // Debug
                
                setFormData({
                    id: usuarioCompleto.id,
                    nombre: usuarioCompleto.nombre || "",
                    apellido: usuarioCompleto.apellido || "",
                    email: usuarioCompleto.email || "",
                    password: "",
                    rol: usuarioCompleto.rol || "",
                    tenantIds: usuarioCompleto.tenants?.map(t => t.id) || [],
                    activo: usuarioCompleto.activo !== undefined ? usuarioCompleto.activo : true,
                });
            } catch (error) {
                console.error("Error al cargar usuario:", error);
                toast.error("Error al cargar los detalles del usuario");
                return;
            }
        } else {
            setEditMode(false);
            setCurrentUsuario(null);
            setFormData({
                id: "",
                nombre: "",
                apellido: "",
                email: "",
                password: "",
                rol: "",
                tenantIds: [],
                activo: true,
            });
        }
        setFormErrors({});
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditMode(false);
        setCurrentUsuario(null);
        setFormErrors({});
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));

        if (formErrors[name]) {
            setFormErrors((prev) => ({ ...prev, [name]: "" }));
        }
    };

    const handleTenantChange = (tenantId) => {
        setFormData((prev) => {
            const tenantIds = prev.tenantIds.includes(tenantId)
                ? prev.tenantIds.filter(id => id !== tenantId)
                : [...prev.tenantIds, tenantId];
            return { ...prev, tenantIds };
        });
    };

    const validateForm = () => {
        const errors = {};

        if (!formData.nombre || formData.nombre.trim() === "") {
            errors.nombre = "El nombre es obligatorio";
        }

        if (!formData.apellido || formData.apellido.trim() === "") {
            errors.apellido = "El apellido es obligatorio";
        }

        if (!formData.email || formData.email.trim() === "") {
            errors.email = "El correo electrónico es obligatorio";
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            errors.email = "El formato del correo electrónico no es válido";
        }

        if (!editMode && (!formData.password || formData.password.length < 6)) {
            errors.password = "La contraseña debe tener al menos 6 caracteres";
        }

        if (!formData.rol) {
            errors.rol = "Debe seleccionar un rol";
        }

        // Solo Owner debe seleccionar tenants manualmente
        // Admin auto-asigna su tenant en el backend
        const isOwner = user?.roles?.includes("Owner");
        if (isOwner && formData.rol !== "Owner" && formData.tenantIds.length === 0) {
            errors.tenantIds = "Debe asignar al menos un tenant para usuarios que no son Owner";
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setSubmitting(true);

        try {
            const usuarioData = {
                Nombre: formData.nombre,
                Apellido: formData.apellido,
                Email: formData.email,
                Rol: formData.rol,
                TenantIds: formData.rol === "Owner" ? [] : formData.tenantIds,
                Activo: formData.activo,
            };

            if (!editMode) {
                usuarioData.Password = formData.password;
            }

            if (editMode) {
                await updateUsuario(currentUsuario.id, usuarioData, user.token);
                toast.success("Usuario actualizado correctamente");
            } else {
                await createUsuario(usuarioData, user.token);
                toast.success("Usuario creado correctamente");
            }

            handleCloseModal();
            loadData();
        } catch (error) {
            console.error("Error al guardar usuario:", error);
            
            if (error.response?.data) {
                const errorData = error.response.data;
                
                if (errorData.errors) {
                    const backendErrors = {};
                    Object.keys(errorData.errors).forEach(key => {
                        const fieldName = key.toLowerCase().replace('$.', '');
                        const messages = errorData.errors[key];
                        backendErrors[fieldName] = Array.isArray(messages) ? messages[0] : messages;
                    });
                    setFormErrors(backendErrors);
                    toast.error("Por favor corrija los errores en el formulario");
                } else if (typeof errorData === 'string') {
                    toast.error(errorData);
                } else {
                    toast.error("Error al guardar el usuario");
                }
            } else {
                toast.error("Error al guardar el usuario");
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("¿Estás seguro de que deseas desactivar este usuario?")) {
            return;
        }

        try {
            await deleteUsuario(id, user.token);
            toast.success("Usuario desactivado correctamente");
            loadData();
        } catch (error) {
            console.error("Error al eliminar usuario:", error);
            toast.error("Error al desactivar el usuario");
        }
    };

    if (loading) {
        return (
            <div className="usuarios-loading">
                <Spinner animation="border" variant="primary" />
                <p>Cargando usuarios...</p>
            </div>
        );
    }

    return (
        <>
            <Head title="Configuración - Usuarios" />
            <div className="usuarios-container">
                <div className="usuarios-header">
                    <h2>Gestión de Usuarios</h2>
                    <div className="usuarios-actions">
                        <Form.Check
                            type="checkbox"
                            label="Incluir inactivos"
                            checked={incluirInactivos}
                            onChange={(e) => setIncluirInactivos(e.target.checked)}
                            className="me-3"
                        />
                        <Button variant="primary" onClick={() => handleShowModal()}>
                            Nuevo Usuario
                        </Button>
                    </div>
                </div>

                {usuarios.length === 0 ? (
                    <Alert variant="info">No hay usuarios registrados.</Alert>
                ) : (
                    <div className="usuarios-table-container">
                        <Table striped bordered hover responsive>
                            <thead>
                                <tr>
                                    <th>Nombre Completo</th>
                                    <th>Correo Electrónico</th>
                                    <th>Rol</th>
                                    {user?.roles?.includes("Owner") && <th>Tenants</th>}
                                    <th>Estado</th>
                                    <th>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {usuarios.map((usuario) => (
                                    <tr key={usuario.id} className={!usuario.activo ? "usuario-inactive" : ""}>
                                        <td>{usuario.nombreCompleto}</td>
                                        <td>{usuario.email}</td>
                                        <td>
                                            <Badge bg={
                                                usuario.rol === "Owner" ? "danger" :
                                                usuario.rol === "Administrador" ? "warning" : "info"
                                            }>
                                                {usuario.rol}
                                            </Badge>
                                        </td>
                                        {user?.roles?.includes("Owner") && (
                                            <td>
                                                {usuario.rol === "Owner" ? (
                                                    <span className="text-muted">Todos</span>
                                                ) : usuario.tenantsNombres && usuario.tenantsNombres.length > 0 ? (
                                                    <div className="d-flex flex-wrap gap-1">
                                                        {usuario.tenantsNombres.map((nombre, idx) => (
                                                            <Badge key={idx} bg="secondary">
                                                                {nombre}
                                                            </Badge>
                                                        ))}
                                                    </div>
                                                ) : (
                                                    <span className="text-muted">Sin tenant</span>
                                                )}
                                            </td>
                                        )}
                                        <td>
                                            <Badge bg={usuario.activo ? "success" : "secondary"}>
                                                {usuario.activo ? "Activo" : "Inactivo"}
                                            </Badge>
                                        </td>
                                        <td className="usuarios-actions-cell">
                                            <Button
                                                variant="warning"
                                                size="sm"
                                                onClick={() => handleShowModal(usuario)}
                                                className="me-2"
                                            >
                                                Editar
                                            </Button>
                                            {usuario.activo && (
                                                <Button
                                                    variant="danger"
                                                    size="sm"
                                                    onClick={() => handleDelete(usuario.id)}
                                                >
                                                    Desactivar
                                                </Button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                )}

                {/* Modal para Crear/Editar */}
                <Modal show={showModal} onHide={handleCloseModal} size="lg">
                    <Modal.Header closeButton>
                        <Modal.Title>{editMode ? "Editar Usuario" : "Nuevo Usuario"}</Modal.Title>
                    </Modal.Header>
                    <Form onSubmit={handleSubmit}>
                        <Modal.Body>
                            <Form.Group className="mb-3">
                                <Form.Label>Nombre *</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="nombre"
                                    value={formData.nombre}
                                    onChange={handleInputChange}
                                    isInvalid={!!formErrors.nombre}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {formErrors.nombre}
                                </Form.Control.Feedback>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Apellido *</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="apellido"
                                    value={formData.apellido}
                                    onChange={handleInputChange}
                                    isInvalid={!!formErrors.apellido}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {formErrors.apellido}
                                </Form.Control.Feedback>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Correo Electrónico *</Form.Label>
                                <Form.Control
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    isInvalid={!!formErrors.email}
                                    disabled={editMode}
                                />
                                <Form.Control.Feedback type="invalid">
                                    {formErrors.email}
                                </Form.Control.Feedback>
                            </Form.Group>

                            {!editMode && (
                                <Form.Group className="mb-3">
                                    <Form.Label>Contraseña *</Form.Label>
                                    <Form.Control
                                        type="password"
                                        name="password"
                                        value={formData.password}
                                        onChange={handleInputChange}
                                        isInvalid={!!formErrors.password}
                                        placeholder="Mínimo 6 caracteres"
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {formErrors.password}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            )}

                            <Form.Group className="mb-3">
                                <Form.Label>Rol *</Form.Label>
                                <Form.Select
                                    name="rol"
                                    value={formData.rol}
                                    onChange={handleInputChange}
                                    isInvalid={!!formErrors.rol}
                                >
                                    <option value="">Seleccione un rol</option>
                                    {roles.map((rol) => (
                                        <option key={rol} value={rol}>{rol}</option>
                                    ))}
                                </Form.Select>
                                <Form.Control.Feedback type="invalid">
                                    {formErrors.rol}
                                </Form.Control.Feedback>
                            </Form.Group>

                            {/* Solo Owner puede seleccionar tenants. Admin asigna automáticamente su tenant */}
                            {formData.rol && formData.rol !== "Owner" && user?.roles?.includes("Owner") && (
                                <Form.Group className="mb-3">
                                    <Form.Label>Tenants Asignados *</Form.Label>
                                    <div className="tenants-checkbox-group">
                                        {tenants.map((tenant) => (
                                            <Form.Check
                                                key={tenant.id}
                                                type="checkbox"
                                                label={`${tenant.dNomEmi} (${tenant.ruc})`}
                                                checked={formData.tenantIds.includes(tenant.id)}
                                                onChange={() => handleTenantChange(tenant.id)}
                                            />
                                        ))}
                                    </div>
                                    {formErrors.tenantIds && (
                                        <div className="text-danger small mt-1">{formErrors.tenantIds}</div>
                                    )}
                                </Form.Group>
                            )}
                            
                            {/* Mensaje informativo para Admin */}
                            {formData.rol && formData.rol !== "Owner" && !user?.roles?.includes("Owner") && (
                                <Alert variant="info" className="mb-3">
                                    <small>
                                        <i className="bi bi-info-circle me-2"></i>
                                        El usuario será asignado automáticamente a su tenant.
                                        <br />
                                        <strong>Nota:</strong> Puede crear usuarios de tipo "Administrador" y "Cliente".
                                    </small>
                                </Alert>
                            )}

                            {editMode && (
                                <Form.Group className="mb-3">
                                    <Form.Check
                                        type="checkbox"
                                        name="activo"
                                        label="Activo"
                                        checked={formData.activo}
                                        onChange={handleInputChange}
                                    />
                                </Form.Group>
                            )}
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={handleCloseModal} disabled={submitting}>
                                Cancelar
                            </Button>
                            <Button variant="primary" type="submit" disabled={submitting}>
                                {submitting ? (
                                    <>
                                        <Spinner as="span" animation="border" size="sm" className="me-2" />
                                        Guardando...
                                    </>
                                ) : editMode ? (
                                    "Actualizar"
                                ) : (
                                    "Crear"
                                )}
                            </Button>
                        </Modal.Footer>
                    </Form>
                </Modal>
            </div>
        </>
    );
};

export default UsuariosPage;
